import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo, useState } from 'react';
import { noop } from 'lodash-es';
import { Button, DataTable, FilterPickerGroup, Page, PageContentHeader, PageEmptyState, PageErrorState } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { CreateProposalButton } from '~/components/CreateProposalButton/CreateProposalButton';
import { EmptyStateIllustration } from '~/components/EmptyStateIllustration/EmptyStateIllustration';
import PageLoadingState from '~/components/PageLoadingState';
import TableFilters from '~/components/TableFilters/TableFilters';
import { SearchFilterType } from '~/components/TableFilters/types';
import { useAccountingCustomers } from '~/hooks/reactQuery/queries/useAccountingCustomers';
import { useContacts } from '~/hooks/reactQuery/queries/useContacts';
import { useIntegrationsSummary } from '~/hooks/reactQuery/queries/useIntegrationsSummary';
import { useContactArchiveRestoreDelete } from '~/hooks/useContactArchiveRestoreDelete';
import { useDebouncedLogSearchToBI } from '~/hooks/useDebouncedLogSearchToBI';
import { useIsContactEmailUpdateInProgress } from '~/hooks/usePendingContactEmailUpdates';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useSortFromUrl } from '~/hooks/useSortFromUrl';
import { useStateFromUrl } from '~/hooks/useStateFromUrl';
import { useTableColumnMenu } from '~/hooks/useTableColumnMenu';
import { useLocaleKeys } from '~/i18n';
import { ContactStatus } from '~/models/ContactModel';
import { selectors } from '~/store/state-selectors';
import { useStore, useStoreActions } from '~/store/store-context';
import { Accounting } from '~/types/accounting';
import { contactMenuActions, showsActionsMenu } from './ContactsDashboard.rowActions';
import { CONTACT_COLUMN_NAMES, CONTACT_MENU_LABELS, CONTACT_ROW_HEAP_ID, DEFAULT_CONTACTS_SORT, SAMPLE_CONTACT_ROW_HEAP_ID, integrationTooltipText, contactColumns, filterContacts, sortContacts, toContactRows } from './ContactsDashboard.table.utils';
import { eligibilityTooltipsTextsPath } from './ContactsDashboardOld.utils';
import { useLegacyContactActions } from './hooks/useLegacyContactActions';
export const GetContactsDashboardTID = (parentTestId)=>getTestIds(parentTestId);
const PAGE_SIZE = 100;
const MOBILE_FIELDS = [
    {
        name: 'companyName'
    },
    {
        name: 'status'
    },
    {
        name: 'primaryContactName'
    },
    {
        name: 'contactDetails'
    }
];
function ContactsTable(param) {
    let { contacts, isRefreshing, refresh } = param;
    const { ContactsDashboardTestIds: TID } = useMemo(()=>GetContactsDashboardTID(), []);
    const { logBI } = useStoreActions();
    const { t: texts } = useLocaleKeys('client.contactsDashboardOld');
    const { t: columnHeaderTexts } = useLocaleKeys('client.contactsDashboardOld.columnHeaders');
    const { t: columnsTexts } = useLocaleKeys('client.contactsDashboardOld.columns');
    const { t: statusFilterTexts } = useLocaleKeys('client.contactsDashboardOld.contactStatusFilter');
    const { t: emptyStateTexts } = useLocaleKeys('client.contactsDashboardOld.emptyState');
    const { t: rowTexts } = useLocaleKeys('client.contactsDashboardOld.row');
    const { t: clientViewTexts } = useLocaleKeys('client.view');
    const { t: commonButtonTexts } = useLocaleKeys('common.buttons');
    const { t: eligibilityTooltipsTexts } = useLocaleKeys(eligibilityTooltipsTextsPath);
    const [page, setPage] = useState(1);
    const debouncedLogSearchToBI = useDebouncedLogSearchToBI(11602);
    const defaultSort = useMemo(()=>({
            colIndex: CONTACT_COLUMN_NAMES.indexOf(DEFAULT_CONTACTS_SORT.name),
            sort: DEFAULT_CONTACTS_SORT.direction
        }), []);
    const { sort: urlSort, changeSort } = useSortFromUrl({
        defaultSort,
        sortIds: CONTACT_COLUMN_NAMES
    });
    const { urlState: { search, status: statusFilterValue }, changeUrlState } = useStateFromUrl({
        search: {
            defaultValue: ''
        },
        status: {
            defaultValue: [
                ContactStatus.Active
            ],
            multi: true
        }
    });
    const showQboIntegration = useStore(selectors.integration.showQboIntegration);
    const showXeroIntegration = useStore(selectors.integration.showXeroIntegration);
    useIntegrationsSummary({
        enabled: showQboIntegration || showXeroIntegration,
        refreshOnHookMount: true
    });
    const { customers: qboCustomers } = useAccountingCustomers(Accounting.QBO, {
        enabled: showQboIntegration,
        refreshOnHookMount: true
    });
    const { customers: xeroCustomers } = useAccountingCustomers(Accounting.Xero, {
        enabled: showXeroIntegration,
        refreshOnHookMount: true
    });
    const qboDisplayById = useMemo(()=>Object.fromEntries((qboCustomers !== null && qboCustomers !== void 0 ? qboCustomers : []).map((customer)=>[
                customer.id(),
                customer.formattedDisplay()
            ])), [
        qboCustomers
    ]);
    const xeroDisplayById = useMemo(()=>Object.fromEntries((xeroCustomers !== null && xeroCustomers !== void 0 ? xeroCustomers : []).map((customer)=>[
                customer.id(),
                customer.formattedDisplay()
            ])), [
        xeroCustomers
    ]);
    const statusFilterOptions = useMemo(()=>[
            {
                value: ContactStatus.Active,
                label: statusFilterTexts.active()
            },
            {
                value: ContactStatus.Archived,
                label: statusFilterTexts.archived()
            }
        ], []);
    const filterConfig = useMemo(()=>({
            status: {
                type: SearchFilterType.MultiSelectFilter,
                removable: false,
                options: statusFilterOptions,
                mobile: {
                    isEmptyValue: (value)=>!Array.isArray(value) || value.length === 0 || value.length === statusFilterOptions.length
                }
            }
        }), [
        statusFilterOptions
    ]);
    const sortOptions = useMemo(()=>[
            {
                id: 'companyName',
                label: columnsTexts.clientName(),
                defaultDirection: 'desc',
                colIndex: 0
            },
            {
                id: 'status',
                label: columnsTexts.status(),
                defaultDirection: 'desc',
                colIndex: 1
            },
            {
                id: 'primaryContactName',
                label: columnsTexts.contactName(),
                defaultDirection: 'desc',
                colIndex: 2
            },
            {
                id: 'contactDetails',
                label: columnsTexts.contactDetails(),
                defaultDirection: 'desc',
                colIndex: 3
            }
        ], []);
    const tableSort = useMemo(()=>{
        var _CONTACT_COLUMN_NAMES_, _ref;
        return {
            name: (_CONTACT_COLUMN_NAMES_ = CONTACT_COLUMN_NAMES[(_ref = urlSort === null || urlSort === void 0 ? void 0 : urlSort.colIndex) !== null && _ref !== void 0 ? _ref : defaultSort.colIndex]) !== null && _CONTACT_COLUMN_NAMES_ !== void 0 ? _CONTACT_COLUMN_NAMES_ : DEFAULT_CONTACTS_SORT.name,
            direction: (urlSort === null || urlSort === void 0 ? void 0 : urlSort.sort) === 'asc' ? 'asc' : 'desc'
        };
    }, [
        urlSort === null || urlSort === void 0 ? void 0 : urlSort.colIndex,
        urlSort === null || urlSort === void 0 ? void 0 : urlSort.sort,
        defaultSort.colIndex
    ]);
    const visibleContacts = useMemo(()=>sortContacts({
            contacts: filterContacts({
                contacts,
                search,
                statuses: statusFilterValue
            }),
            sort: tableSort
        }), [
        contacts,
        search,
        statusFilterValue,
        tableSort
    ]);
    const contactById = useMemo(()=>new Map(visibleContacts.map((contact)=>[
                contact.id(),
                contact
            ])), [
        visibleContacts
    ]);
    const sampleIds = useMemo(()=>new Set(visibleContacts.filter((contact)=>contact.isSample()).map((contact)=>contact.id())), [
        visibleContacts
    ]);
    const rows = useMemo(()=>toContactRows({
            contacts: visibleContacts,
            statusTexts: statusFilterTexts,
            showQbo: showQboIntegration,
            showXero: showXeroIntegration,
            qboDisplayById,
            xeroDisplayById
        }), [
        visibleContacts,
        showQboIntegration,
        showXeroIntegration,
        qboDisplayById,
        xeroDisplayById
    ]);
    const pagedRows = useMemo(()=>rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [
        rows,
        page
    ]);
    const { mainColumn, columns } = useMemo(()=>contactColumns({
            texts: columnHeaderTexts,
            integrationTooltip: integrationTooltipText
        }), []);
    const contactActions = useLegacyContactActions({
        contacts,
        refresh
    });
    const { onDelete: onDeleteContact } = useContactArchiveRestoreDelete({
        onSuccess: refresh
    });
    const isEmailUpdateInProgress = useIsContactEmailUpdateInProgress(null);
    const menuTexts = useMemo(()=>({
            edit: commonButtonTexts.edit(),
            delete: CONTACT_MENU_LABELS.delete,
            viewClientPortal: clientViewTexts.menuItem()
        }), []);
    const columnMenu = useTableColumnMenu('contacts');
    const onClearFilters = ()=>{
        changeUrlState({
            search: '',
            status: statusFilterOptions.map((option)=>option.value)
        });
        setPage(1);
    };
    const handleTableFiltersRefresh = (filters, sort)=>{
        const selectedStatus = filters.status;
        changeUrlState({
            status: (selectedStatus === null || selectedStatus === void 0 ? void 0 : selectedStatus.length) ? selectedStatus : statusFilterOptions.map((option)=>option.value)
        });
        if (sort !== undefined) changeSort(sort);
        setPage(1);
    };
    return /*#__PURE__*/ _jsxs(Page, {
        testId: TID.Root(),
        variant: "containedHeight",
        onRefresh: refresh,
        header: texts.title(),
        loading: isRefreshing,
        children: [
            /*#__PURE__*/ _jsx(PageContentHeader, {
                children: /*#__PURE__*/ _jsx(FilterPickerGroup, {
                    children: /*#__PURE__*/ _jsx(TableFilters, {
                        testIdPrefix: TID.Root(),
                        filterLabels: {
                            status: statusFilterTexts.label()
                        },
                        filterConfig: filterConfig,
                        selectedFilters: {
                            status: statusFilterValue
                        },
                        setSelectedFilters: noop,
                        onRefresh: handleTableFiltersRefresh,
                        search: {
                            bi: {
                                name: 'contacts_search'
                            },
                            value: search,
                            testId: TID.Search(),
                            registerHotkey: contactActions.registerSearchBoxHotkey,
                            onChange: (event)=>{
                                changeUrlState({
                                    search: event.target.value
                                });
                                debouncedLogSearchToBI.onChange(event.target.value);
                                setPage(1);
                            },
                            onClear: ()=>{
                                changeUrlState({
                                    search: ''
                                });
                                debouncedLogSearchToBI.onClear();
                                setPage(1);
                            }
                        },
                        mobile: {
                            sortOptions,
                            sort: urlSort
                        }
                    })
                })
            }),
            /*#__PURE__*/ _jsx(DataTable, {
                allowColumnResize: true,
                allowColumnReorder: true,
                reorderBi: {
                    name: 'contacts_reorder_columns'
                },
                allowColumnHide: true,
                columnMenu: columnMenu,
                mainColumn: mainColumn,
                columns: columns,
                items: pagedRows,
                getRowId: (row)=>row.id,
                mobile: {
                    fields: MOBILE_FIELDS
                },
                rowHeapId: (row)=>sampleIds.has(row.id) ? SAMPLE_CONTACT_ROW_HEAP_ID : CONTACT_ROW_HEAP_ID,
                state: rows.length === 0 ? 'empty' : 'default',
                sort: {
                    name: tableSort.name,
                    direction: tableSort.direction,
                    sortedBy: 'consumer',
                    onChange: (nextSort)=>{
                        logBI(11603, {
                            column: nextSort.name
                        });
                        changeSort({
                            colIndex: CONTACT_COLUMN_NAMES.indexOf(nextSort.name),
                            sort: nextSort.direction
                        });
                        setPage(1);
                    }
                },
                rowActions: {
                    menuBi: {
                        name: 'contact_actions'
                    },
                    menuTitle: (row)=>row.companyName,
                    menu: (row)=>{
                        const contact = contactById.get(row.id);
                        if (contact == null || !showsActionsMenu(contact)) return [];
                        return contactMenuActions({
                            contact,
                            isEmailUpdateInProgress,
                            texts: menuTexts,
                            rowTexts,
                            clientViewTexts,
                            eligibilityTooltipsTexts,
                            onEdit: contactActions.openContactModal,
                            onAddToAnotherClient: contactActions.openAddToAnotherClientModal,
                            onDelete: onDeleteContact,
                            onStartClientView: contactActions.startClientView
                        });
                    }
                },
                pagination: {
                    page,
                    pageSize: PAGE_SIZE,
                    total: rows.length,
                    onPageChange: setPage
                },
                emptyState: /*#__PURE__*/ _jsx(PageEmptyState, {
                    illustration: /*#__PURE__*/ _jsx(EmptyStateIllustration, {}),
                    title: emptyStateTexts.noMatchTitle(),
                    message: emptyStateTexts.noMatchMessage(),
                    actions: /*#__PURE__*/ _jsx(Button, {
                        bi: {
                            name: 'clear_contacts_filters'
                        },
                        testId: TID.EmptySearchClearButton(),
                        onClick: onClearFilters,
                        children: emptyStateTexts.clearFilters()
                    })
                }),
                testId: TID.Table()
            })
        ]
    });
}
export function ContactsDashboard() {
    const { contacts, isLoading, isFetching, refresh, error } = useContacts({
        refreshOnMount: true
    });
    const { ContactsDashboardTestIds: TID } = useMemo(()=>GetContactsDashboardTID(), []);
    const { t: texts } = useLocaleKeys('client.contactsDashboardOld');
    const { t: emptyStateTexts } = useLocaleKeys('client.contactsDashboardOld.emptyState');
    useSetDocumentTitle(texts.title());
    const handleRefresh = useCallback(async ()=>{
        const result = await refresh();
        return result.data;
    }, [
        refresh
    ]);
    if (isLoading) {
        return /*#__PURE__*/ _jsx(PageLoadingState, {
            header: texts.title()
        });
    }
    if (error) {
        return /*#__PURE__*/ _jsx(PageErrorState, {
            testId: TID.ErrorStatePage(),
            title: texts.title(),
            onRefresh: handleRefresh,
            pullToRefresh: true
        });
    }
    if (contacts == null || contacts.length === 0) {
        return /*#__PURE__*/ _jsx(Page, {
            testId: TID.Root(),
            header: texts.title(),
            loading: isFetching,
            onRefresh: handleRefresh,
            variant: "containedHeight",
            children: /*#__PURE__*/ _jsx(PageEmptyState, {
                maxHeight: true,
                testId: TID.EmptyStatePage(),
                illustration: /*#__PURE__*/ _jsx(EmptyStateIllustration, {}),
                message: emptyStateTexts.noContactsMessage(),
                actions: /*#__PURE__*/ _jsx(CreateProposalButton, {
                    testId: TID.EmptyStateNewButton(),
                    text: emptyStateTexts.createAgreement()
                })
            })
        });
    }
    return /*#__PURE__*/ _jsx(ContactsTable, {
        contacts: contacts,
        isRefreshing: isFetching,
        refresh: handleRefresh
    });
}
